import { useToast } from 'vue-toastification'

const STORAGE_KEY = 'portal:chunk-reload-at'
const DEBOUNCE_MS = 15_000
const RELOAD_DELAY_MS = 1_500

const STALE_CHUNK_PATTERN =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|MIME type|Couldn't resolve component/i

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  if (error && typeof error === 'object' && 'message' in error) {
    const msg = (error as { message: unknown }).message
    if (typeof msg === 'string') return msg
  }
  return String(error ?? '')
}

/** True when a dynamic import / module script failed due to a stale deploy (missing hashed chunk). */
export function isStaleChunkError(error: unknown): boolean {
  return STALE_CHUNK_PATTERN.test(errorMessage(error))
}

function lastAttemptAt(): number {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return 0
    const n = Number(raw)
    return Number.isFinite(n) ? n : 0
  } catch {
    return 0
  }
}

function markAttempt(): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, String(Date.now()))
  } catch {
    // sessionStorage may be unavailable; still attempt recovery once
  }
}

function withinDebounceWindow(): boolean {
  return Date.now() - lastAttemptAt() < DEBOUNCE_MS
}

function showHardRefreshPrompt(): void {
  // Patients are mostly on phones, where there is no keyboard shortcut to name.
  useToast().error('Part of the portal failed to load. Please refresh the page.')
}

function showReloadingToast(): void {
  useToast().info('A new version was released. Reloading…')
}

/**
 * Recover from a stale chunk by navigating to `fullPath` once (lands on the clicked route).
 * Debounced via sessionStorage to avoid reload loops on a bad deploy.
 */
export function navigateOnceForStaleChunks(fullPath: string): void {
  if (withinDebounceWindow()) {
    console.error('[chunk-load] reload already attempted; not looping')
    showHardRefreshPrompt()
    return
  }
  markAttempt()
  showReloadingToast()
  window.setTimeout(() => {
    window.location.assign(fullPath)
  }, RELOAD_DELAY_MS)
}

/**
 * Recover from a stale chunk by reloading the current page once.
 * Used for unhandledrejection (no target route).
 */
export function reloadOnceForStaleChunks(): void {
  if (withinDebounceWindow()) {
    console.error('[chunk-load] reload already attempted; not looping')
    showHardRefreshPrompt()
    return
  }
  markAttempt()
  showReloadingToast()
  window.setTimeout(() => {
    window.location.reload()
  }, RELOAD_DELAY_MS)
}

/** Clear the recovery flag only when outside the debounce window (preserves anti-loop after a recovery reload). */
export function clearChunkReloadAttempt(): void {
  if (withinDebounceWindow()) return
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}
